import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import style from "./restore-version-dialog.scss";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import { TitleIcon } from "components/icons/TitleIcon";
import layout from "components/layout/layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import buttons from "styles/buttons.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface RestoreVersionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    workflowName?: string;
    versionNumber: string;
    supportsEditorRestore: boolean;
    isRestoring?: boolean;
    restoreSuccess?: boolean;
    restoreError?: string | null;
}

const getModalTitle = (restoreSuccess: boolean | undefined, restoreError: string | null | undefined, t: TFunction) => {
    if (restoreSuccess) {
        return t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.successTitle");
    }
    if (restoreError) {
        return t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.errorTitle");
    }
    return t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.title");
};

const getTitleIcon = (restoreSuccess: boolean | undefined, restoreError: string | null | undefined) => {
    if (restoreSuccess) {
        return <TitleIcon iconType="SUCCESS" />;
    }
    if (restoreError) {
        return <TitleIcon iconType="ERROR" />;
    }
    return undefined;
};

const getDialogContent = (supportsEditorRestore: boolean, t: TFunction) => {
    if (supportsEditorRestore) {
        return {
            intro: t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.introductionText"),
            confirmation: t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.confirmationText"),
        };
    } else {
        return {
            intro: t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.noPreviewIntroText"),
            confirmation: t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.confirmationText"),
        };
    }
};

export function RestoreVersionDialog(props: RestoreVersionDialogProps): JSX.Element {
    const { t } = useTranslation();
    const theme = useTheme();
    const modalTitle = getModalTitle(props.restoreSuccess, props.restoreError, t);
    const titleIcon = getTitleIcon(props.restoreSuccess, props.restoreError);
    const mailToLink = `mailto:${SUPPORT_EMAIL}`;

    if (props.isRestoring) {
        return (
            <Modal isOpen={props.isOpen} hideModal={() => {}} modalTitle={modalTitle}>
                <div className={style.dialogContainer}>
                    <LoadingIndicator />
                </div>
            </Modal>
        );
    }

    if (props.restoreSuccess) {
        const successMessage = t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.successMessage", {
            name: props.workflowName,
            version: props.versionNumber,
        });

        toast(
            <SuccessIcon
                successClass={layout.customToastSuccessIcon}
                color={theme.contentBackgroundColor}
                text={successMessage}
            />,
            {
                closeButton: (closeToastProps) => (
                    <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                ),
                className: layout.customToastSuccessMessage,
            }
        );
        props.onClose();
        return <></>;
    }

    if (props.restoreError) {
        return (
            <Modal isOpen={props.isOpen} hideModal={props.onClose} modalTitle={modalTitle} titleIcon={titleIcon}>
                <div className={style.dialogContainer}>
                    <span>
                        {t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.errorMessage")}
                        <a href={mailToLink}>{t("Common.genericContactSupport")} </a>
                    </span>
                </div>

                <div className={style.buttonContainer}>
                    <button
                        className={buttons.primaryButtonWithoutIcon}
                        onClick={props.onClose}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
        );
    }

    const content = getDialogContent(props.supportsEditorRestore, t);

    return (
        <Modal isOpen={props.isOpen} hideModal={props.onClose} modalTitle={modalTitle} titleIcon={titleIcon}>
            <div className={style.dialogContainer}>{content.intro}</div>
            <div className={style.dialogContainerLast}>{content.confirmation}</div>
            <div className={style.buttonContainer}>
                <button
                    className={buttons.primaryButtonWithoutIcon}
                    onClick={props.onConfirm}
                    data-testid={testIds.common.confirmationDialog.confirmButton}
                >
                    {t("WorkflowsTable.sidePanel.tabs.versionHistory.restoreVersionDialog.restoreAndOverwriteButton")}
                </button>
                <button
                    className={buttons.secondaryButtonWithoutIcon}
                    onClick={props.onClose}
                    data-testid={testIds.common.dialog.closeButton}
                >
                    {t("Common.cancel")}
                </button>
            </div>
        </Modal>
    );
}

export default RestoreVersionDialog;
